import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearch } from '@components/reducers/search';
import TableContainer from '@mui/material/TableContainer';
import { CustomerOrderTotalPrice, FormatDate } from '@components/Shared/Utils';
import { GetAccountSettings, GetCommonSettings } from '@themes/common/data';
import useCustomerReducer from '../useCustomerReducer';
import { AccountPaperContainer } from '../components';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useLocation } from 'react-router-dom';
import { DataGrid, } from '@mui/x-data-grid';
import { parseISO } from 'date-fns';
import { CleanupFilterItems } from '@commons/filters/frontend';
import CustomerOrderStatus from 'Stenbolaget-IntegrationService/Models/CustomerOrderStatus.cs';
import { FormatMoney } from '@components/Shared/Utils/price';
const translations = GetAccountSettings().translations;
const { customers_order_order_number, customers_order_date, customers_order_amount_with_vat, customers_order_marking, customers_order_status, customers_delivery_delivery_options, } = translations;
const Orders = (_) => {
    const location = useLocation();
    const navigate = useNavigate();
    const settings = GetCommonSettings();
    const [{ customer }] = useCustomerReducer();
    const [sort, setSort] = useState([]);
    const [filters, setFilters] = useState({
        items: [],
    });
    const params = useMemo(() => {
        return { customerId: customer === null || customer === void 0 ? void 0 : customer.id };
    }, [customer === null || customer === void 0 ? void 0 : customer.id]);
    const [state, { setPageAndLimit, setExtraFilters }] = useSearch(`${process.env.HOST}/api/orders`, 'customer_orders', params);
    const pagination = useMemo(() => ({
        page: state.filters.page - 1,
        pageSize: state.filters.limit,
    }), [state.filters.limit, state.filters.page]);
    const handlePaginationModelChange = useCallback((e) => {
        setPageAndLimit(e.page + 1, e.pageSize);
    }, [setPageAndLimit]);
    const handleRowClick = useCallback((params) => {
        navigate(`${location.pathname}?id=${params.row.id}`);
    }, [location.pathname, navigate]);
    const statusOptions = useMemo(() => [
        CustomerOrderStatus.Registered,
        CustomerOrderStatus.OrderConfirmationPrinted,
        CustomerOrderStatus.CanNotConfirmDeliveryTime,
        CustomerOrderStatus.DeliveryNotePrinted,
        CustomerOrderStatus.PartiallyDelivered,
        CustomerOrderStatus.History,
    ].map((status) => ({
        value: status,
        label: translations[`monitor_customer_order_status_${status}`],
    })), []);
    const columns = useMemo(() => [
        {
            field: 'orderNumber',
            headerName: customers_order_order_number,
            width: 150,
        },
        {
            field: 'orderDate',
            headerName: customers_order_date,
            type: 'date',
            renderCell: (params) => FormatDate(params.row.orderDate, 'yyyy-MM-dd'),
            width: 120,
            valueGetter: (params) => params.row.orderDate ? parseISO(params.row.orderDate.toString()) : null,
        },
        {
            field: 'businessContactOrderNumber',
            headerName: customers_order_marking,
            width: 200,
        },
        {
            field: 'status',
            headerName: customers_order_status,
            renderCell: (params) => translations[`monitor_customer_order_status_${params.row.status}`],
            width: 150,
            type: 'singleSelect',
            valueOptions: statusOptions,
            sortable: false,
        },
        {
            field: 'deliveryMethod.description',
            headerName: customers_delivery_delivery_options,
            valueGetter: (params) => { var _a; return (_a = params.row.deliveryMethod) === null || _a === void 0 ? void 0 : _a.description; },
            width: 250,
        },
        {
            field: 'totalPrice',
            headerName: customers_order_amount_with_vat,
            type: 'number',
            valueGetter: (params) => CustomerOrderTotalPrice(params.row),
            renderCell: (params) => FormatMoney(CustomerOrderTotalPrice(params.row) * 100, settings.money_format),
            width: 150,
            filterable: false,
            sortable: false,
        },
        {
            field: 'id',
            headerName: '',
            width: 50,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (_jsx(ChevronRightIcon, { sx: { cursor: 'pointer' }, onClick: () => {
                    navigate(`${location.pathname}?id=${params.row.id}`);
                } })),
        },
    ], [statusOptions, location.pathname, navigate, settings.money_format]);
    useEffect(() => {
        setExtraFilters({
            sort,
            filters: CleanupFilterItems(filters, {
                date: ['orderDate'],
                string: ['orderNumber', 'businessContactOrderNumber', 'deliveryMethod.description'],
                singleSelect: ['status'],
            }),
        });
    }, [filters, setExtraFilters, sort]);
    return (_jsx(TableContainer, Object.assign({ component: AccountPaperContainer }, { children: _jsx(DataGrid, { columns: columns, rows: state.items, initialState: {
                pagination: {
                    paginationModel: { page: 0, pageSize: 25 },
                },
            }, pageSizeOptions: [10, 25, 50, 100, 250], sortModel: sort, filterModel: filters, onSortModelChange: setSort, onFilterModelChange: setFilters, onRowClick: handleRowClick, paginationModel: pagination, onPaginationModelChange: handlePaginationModelChange, loading: state.loading }) })));
};
export default Orders;
